import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Box, Typography, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MiniMonthGrid from './MiniMonthGrid';
import { parseISODate, toISODate } from '../Types/DatePickerHelpers';

/**
 * CalEventDatePicker — single-month selectable calendar popup.
 * Styled like the AppBar mini calendar (CalendarPopup), single month, selectable.
 * @param {object} props
 * @param {{ top: number, left: number }} props.anchorPos
 * @param {string} props.value - current date as YYYY-MM-DD, or ''
 * @param {function} props.onSelect - fn(isoDate)
 * @param {function} props.onClose
 */
export default function CalEventDatePicker({ anchorPos, value, onSelect, onClose }) {
    const today = new Date();
    const parsed = parseISODate(value);

    const [offset, setOffset] = useState(0);
    const popupRef = useRef(null);

    // Start on the month of the current value, if set
    useEffect(() => {
        if (!parsed) return;
        const diff = (parsed.year - today.getFullYear()) * 12 + (parsed.month - today.getMonth());
        setOffset(diff);
    }, []);

    const view = new Date(today.getFullYear(), today.getMonth() + offset, 1);

    // Close on outside click
    useEffect(() => {
        const handleClick = (e) => {
            if (popupRef.current && !popupRef.current.contains(e.target)) onClose();
        };
        const t = setTimeout(() => document.addEventListener('mousedown', handleClick), 50);
        return () => {
            clearTimeout(t);
            document.removeEventListener('mousedown', handleClick);
        };
    }, [onClose]);

    // Close on Escape
    useEffect(() => {
        const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onClose]);

    const handleSelect = (isoDate) => {
        onSelect(isoDate);
        onClose();
    };

    const handleToday = () => {
        setOffset(0);
        handleSelect(toISODate(today.getFullYear(), today.getMonth(), today.getDate()));
    };

    const popup = (
        <Box
            ref={popupRef}
            sx={{
                position: 'fixed',
                top: anchorPos.top,
                left: anchorPos.left,
                zIndex: 10001,
                bgcolor: '#0d1f33',
                border: '0.5px solid #294766',
                borderRadius: '6px',
                boxShadow: '0 8px 28px rgba(0,0,0,0.6)',
                p: '12px 14px 14px',
                userSelect: 'none',
            }}
        >
            {/* Toolbar — prev/next month + vandaag */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '10px' }}>
                <IconButton size="small" onClick={() => setOffset((o) => o - 1)} sx={{ color: '#b0c8e0', '&:hover': { color: '#a3d1ff' } }}>
                    <ChevronLeftIcon fontSize="small" />
                </IconButton>

                <Typography
                    onClick={handleToday}
                    sx={{
                        color: offset === 0 ? '#a3d1ff' : '#66b2ff',
                        fontSize: 11,
                        fontFamily: "'DM Sans', sans-serif",
                        cursor: 'pointer',
                        '&:hover': { color: '#a3d1ff' },
                    }}
                >
                    vandaag
                </Typography>

                <IconButton size="small" onClick={() => setOffset((o) => o + 1)} sx={{ color: '#b0c8e0', '&:hover': { color: '#a3d1ff' } }}>
                    <ChevronRightIcon fontSize="small" />
                </IconButton>
            </Box>

            <MiniMonthGrid
                year={view.getFullYear()}
                month={view.getMonth()}
                today={today}
                selectedDate={value}
                onSelect={handleSelect}
            />
        </Box>
    );

    return createPortal(popup, document.body);
}
